import { useCallback, useMemo } from 'react';

import { useSelectionAnnouncer } from '../useSelectionAnnouncer/useSelectionAnnouncer';

import { type ToggleGroupProps, type ToggleGroupRow } from './ToggleGroup.types';

export type UseToggleGroupLogicInput = Pick<
  ToggleGroupProps,
  'items' | 'value' | 'onValueChange' | 'isDisabled' | 'isReadOnly'
>;

export interface UseToggleGroupLogic {
  rows: readonly ToggleGroupRow[];
  /** A caller that declared no items — worth showing rather than rendering nothing. */
  isEmpty: boolean;
  toggle: (value: string, isOn: boolean) => void;
  announcement: string;
}

/** Keeps only the boolean entries — an object value's other fields are not ours to rewrite. */
function toBooleanMap(current: Record<string, unknown>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [key, entry] of Object.entries(current)) {
    if (typeof entry === 'boolean') out[key] = entry;
  }

  return out;
}

/**
 * A named set of independent booleans over ONE object value —
 * `verification.steps`'s `{idDocument, selfie, interview}`.
 *
 * Two invariants worth keeping:
 *
 *  - **An unknown BOOLEAN key survives.** A stored map may carry keys this build
 *    does not declare (a newer api, a setting mid-migration); a boolean one is
 *    written back untouched, so toggling `selfie` never deletes `interview`.
 *    A non-boolean one is not — see the next rule, which outranks it: the map
 *    this control owns is a map of booleans.
 *  - **A non-boolean entry is dropped, not coerced.** `'yes'` is not `true`, and
 *    writing it back as one would launder bad data into good-looking data.
 */
export function useToggleGroupLogic({
  items,
  value,
  onValueChange,
  isDisabled = false,
  isReadOnly = false,
}: UseToggleGroupLogicInput): UseToggleGroupLogic {
  const { announcement, announceReadOnlyRefusal } = useSelectionAnnouncer();

  const current: Record<string, unknown> = useMemo(
    () => (typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
    [value],
  );

  const rows = useMemo<readonly ToggleGroupRow[]>(
    () =>
      items.map((item) => ({
        value: item.value,
        label: item.label,
        ...(item.description === undefined ? {} : { description: item.description }),
        isChecked: current[item.value] === true,
        isDisabled: isDisabled || item.isDisabled === true,
      })),
    [current, isDisabled, items],
  );

  const toggle = useCallback(
    (key: string, isOn: boolean) => {
      if (isReadOnly) {
        announceReadOnlyRefusal();
        return;
      }

      onValueChange({ ...toBooleanMap(current), [key]: isOn });
    },
    [announceReadOnlyRefusal, current, isReadOnly, onValueChange],
  );

  return { rows, isEmpty: items.length === 0, toggle, announcement };
}
